import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { FormSection } from '../../common/types/form/form-section';
import {
	type FormSectionCondition,
	type FormSectionConditionCheck,
	FormSectionConditionCheckType,
	FormSectionConditionOperator,
	Visibility,
} from '../../common/types/form/form-section';
import {
	type FormCondition,
	type FormConditions,
	FormConditionType,
	type FormSections,
} from '../types/proforma-form';

// Each layout node is a section
export const convertSections = (
	sections: FormSections,
	layout: DocNode[],
	conditions: FormConditions,
): FormSection[] =>
	layout.map((layoutNode, i) => convertSection(sections, layoutNode, conditions, i));

const convertSection = (
	sections: FormSections,
	layoutNode: DocNode,
	conditions: FormConditions,
	i: number,
): FormSection => {
	const section = sections[i];
	const sectionCondition =
		typeof section === 'undefined' || typeof section.conditions === 'undefined'
			? undefined
			: convertConditions(
					section.conditions.flatMap((id) => (id in conditions ? [conditions[id]] : [])),
				);

	return {
		layout: layoutNode,
		...(sectionCondition && { condition: sectionCondition }),
	};
};

const convertConditions = (conditions: FormCondition[]): FormSectionCondition | undefined => {
	if (!conditions || conditions.length === 0) {
		return undefined;
	}

	const choiceIds = conditions[0].i.co?.cIds;
	const isDeprecatedDataStructure = fg('proforma_acl_handle_missing_co_bugfix')
		? typeof choiceIds !== 'undefined' && Object.keys(choiceIds).length !== 0
		: typeof choiceIds === 'undefined' || Object.keys(choiceIds).length !== 0;

	return isDeprecatedDataStructure
		? mapOldConditionsToNewDataStructure(conditions)
		: mapCondition(conditions[0]);
};

const mapOldConditionsToNewDataStructure = (
	conditions: FormCondition[],
): FormSectionCondition | undefined => {
	const checks: FormSectionConditionCheck[] = conditions.flatMap((condition) =>
		// only show type is supported for legacy data structure
		condition.t !== FormConditionType.Show || typeof condition.i.co?.cIds === 'undefined'
			? []
			: Object.entries(condition.i.co.cIds).map(
					([questionId, choiceIds]): FormSectionConditionCheck => ({
						fieldId: questionId,
						// only some_of operator and option value type is supported in legacy
						type: FormSectionConditionCheckType.SOME_OF,
						constraint: choiceIds,
					}),
				),
	);

	if (checks.length === 0) {
		return undefined;
	}

	return {
		checkGroups: [
			{
				operator: FormSectionConditionOperator.AND,
				checks,
			},
		],
		visibility: Visibility.SHOW,
		operator: FormSectionConditionOperator.AND,
	};
};

const mapCondition = (condition: FormCondition): FormSectionCondition | undefined => {
	if (typeof condition.i.operator === 'undefined') {
		return undefined;
	}

	return {
		checkGroups: condition.i.groups ?? [],
		visibility: mapVisibility(condition.t),
		operator: condition.i.operator,
	};
};

const mapVisibility = (visibility: FormConditionType) => {
	switch (visibility) {
		case FormConditionType.Show:
			return Visibility.SHOW;
		case FormConditionType.Hide:
			return Visibility.HIDE;
		default:
			// shouldn't ever happen, here for eslint
			return Visibility.SHOW;
	}
};
